import { Helmet } from "react-helmet-async";

/**
 * Renders dynamic SEO tags (title, description, Open Graph, Twitter card,
 * canonical, hreflang) for the current page. Used by LandingPage to swap meta
 * data per apartment route + language.
 *
 * Expects a flat seo object (use `resolveSeo` from apartmentSeo.js):
 *   { title, description, ogTitle, ogDescription, twitterTitle,
 *     twitterDescription, ogImage, canonical, lang }
 */
export const SeoHead = ({ seo }) => {
    if (!seo) return null;
    const {
        title,
        description,
        ogTitle,
        ogDescription,
        ogImage,
        canonical,
        twitterTitle,
        twitterDescription,
        lang = "de",
    } = seo;

    const ogLocale = lang === "en" ? "en_US" : "de_DE";

    return (
        <Helmet htmlAttributes={{ lang }}>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />

            {/* hreflang alternates – tells Google about the language siblings */}
            <link rel="alternate" hrefLang="de" href={canonical} />
            <link rel="alternate" hrefLang="en" href={canonical} />
            <link rel="alternate" hrefLang="x-default" href={canonical} />

            <meta property="og:title" content={ogTitle || title} />
            <meta
                property="og:description"
                content={ogDescription || description}
            />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:secure_url" content={ogImage} />
            <meta property="og:url" content={canonical} />
            <meta property="og:type" content="website" />
            <meta property="og:locale" content={ogLocale} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={twitterTitle || title} />
            <meta
                name="twitter:description"
                content={twitterDescription || description}
            />
            <meta name="twitter:image" content={ogImage} />
        </Helmet>
    );
};
